class TabbedContent {
  constructor(element) {
    this.container = element;
    this.tabs = element.querySelectorAll('.tabbed-content__tab');
    this.panels = element.querySelectorAll('.tabbed-content__panel');
    this.activeIndex = 0;
    element._tabbedContentInstance = this;
    this.init();
  }

  init() {
    if (!this.tabs.length || !this.panels.length) return;
    this.bindTabEvents();
    this.bindKeyboardEvents();
    this.bindPanelNavEvents();
    this.bindMobileScrollEvents();
    this.bindVideoEvents();
    this.bindSoundToggleEvents();
    this.handleInitialHash();
    this.createLiveRegion();
    this.updateFlowingIndicator(0);
    this.handleResize();
    this.setupVideoVisibilityObserver();
    this.setupPageVisibilityHandler();
    this.enforceActiveVideoState();
    this.updateArrowStates();
  }

  handleResize() {
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        this.updateFlowingIndicator(this.activeIndex);
      }, 100);
    });
  }

  bindTabEvents() {
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', (e) => {
        e.preventDefault();
        this.activateTab(index);
      });
    });
  }

  bindPanelNavEvents() {
    this.container.querySelectorAll('[data-tab-prev]').forEach(btn => {
      btn.addEventListener('click', () => {
        if (window.innerWidth < 600) {
          if (this.activeIndex > 0) {
            this.activateTab(this.activeIndex - 1);
          }
        } else {
          const prev = this.activeIndex - 1 < 0 ? this.tabs.length - 1 : this.activeIndex - 1;
          this.activateTab(prev);
        }
      });
    });
    this.container.querySelectorAll('[data-tab-next]').forEach(btn => {
      btn.addEventListener('click', () => {
        if (window.innerWidth < 600) {
          if (this.activeIndex < this.tabs.length - 1) {
            this.activateTab(this.activeIndex + 1);
          }
        } else {
          const next = this.activeIndex + 1 >= this.tabs.length ? 0 : this.activeIndex + 1;
          this.activateTab(next);
        }
      });
    });
  }

  updateCounter() {
    // Counter text is statically rendered per-panel in Liquid (forloop.index).
    // No JS update needed — each panel always shows its own correct index.
  }

  updateArrowStates() {
    // Mobile arrows stay visually identical regardless of position — no
    // dimming, no disabled state. The nav is a stable UI fixture on mobile.
    this.container.querySelectorAll('.tabbed-content__panel-arrow').forEach(btn => {
      btn.style.opacity = '1';
      btn.style.cursor = 'pointer';
      btn.disabled = false;
    });
  }

  bindMobileScrollEvents() {
    const panelsContainer = this.container.querySelector('.tabbed-content__panels');
    if (!panelsContainer) return;

    const detectActivePanel = () => {
      if (window.innerWidth >= 600) return;
      let closestIndex = 0;
      let minDistance = Infinity;
      this.panels.forEach((panel, index) => {
        const rect = panel.getBoundingClientRect();
        const distance = Math.abs(rect.left + rect.width / 2 - window.innerWidth / 2);
        if (distance < minDistance) {
          minDistance = distance;
          closestIndex = index;
        }
      });
      if (closestIndex !== this.activeIndex) {
        this.activateTab(closestIndex, true);
      }
    };

    // scrollend fires immediately after scroll-snap settles (Chrome 114+, Firefox 109+)
    panelsContainer.addEventListener('scrollend', detectActivePanel);

    // 50ms debounce fallback for Safari which lacks scrollend
    let scrollTimer;
    panelsContainer.addEventListener('scroll', () => {
      if (window.innerWidth >= 600) return;
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(detectActivePanel, 50);
    });
  }

  bindKeyboardEvents() {
    this.container.addEventListener('keydown', (e) => {
      const target = e.target;
      if (!target.classList.contains('tabbed-content__tab')) return;
      
      let newIndex = this.activeIndex;
      
      switch(e.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          newIndex = this.activeIndex - 1;
          if (newIndex < 0) newIndex = this.tabs.length - 1;
          this.activateTab(newIndex);
          this.tabs[newIndex].focus();
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault();
          newIndex = this.activeIndex + 1;
          if (newIndex >= this.tabs.length) newIndex = 0;
          this.activateTab(newIndex);
          this.tabs[newIndex].focus();
          break;
        case 'Home':
          e.preventDefault();
          this.activateTab(0);
          this.tabs[0].focus();
          break;
        case 'End':
          e.preventDefault();
          const lastIndex = this.tabs.length - 1;
          this.activateTab(lastIndex);
          this.tabs[lastIndex].focus();
          break;
      }
    });
  }

  activateTab(index, fromScroll = false) {
    if (index === this.activeIndex) return;

    if (window.innerWidth < 600 && !fromScroll) {
      const panelsContainer = this.container.querySelector('.tabbed-content__panels');
      const targetPanel = this.panels[index];
      if (panelsContainer && targetPanel) {
        // Scroll only the panels container horizontally — never touch the page scroll position
        const containerRect = panelsContainer.getBoundingClientRect();
        const panelRect = targetPanel.getBoundingClientRect();
        const paddingLeft = parseInt(getComputedStyle(panelsContainer).paddingLeft, 10) || 0;
        const delta = panelRect.left - containerRect.left - paddingLeft;
        panelsContainer.scrollBy({ left: delta, behavior: 'smooth' });
      }
    }

    const currentPanel = this.panels[this.activeIndex];
    const newPanel = this.panels[index];
    const isMobile = window.innerWidth < 600;

    this.stopAllVideosInPanel(currentPanel);
    this.updateFlowingIndicator(index);

    // Desktop: add dissolve-out class and wait for its 250ms animation.
    // Mobile: no animation, switch immediately so the new panel never shows blank.
    if (!isMobile) {
      currentPanel.classList.add('is-fading-out');
    }
    const delay = isMobile ? 0 : 250;

    setTimeout(() => {
      this.tabs[this.activeIndex].classList.remove('is-active');
      this.tabs[this.activeIndex].setAttribute('aria-selected', 'false');
      this.tabs[this.activeIndex].setAttribute('tabindex', '-1');
      currentPanel.classList.remove('is-active', 'is-fading-out');

      // On mobile the hidden attribute is overridden by CSS display:flex — use
      // aria-hidden instead so the inactive panel is hidden from the a11y tree.
      if (isMobile) {
        currentPanel.setAttribute('aria-hidden', 'true');
      } else {
        currentPanel.setAttribute('hidden', '');
      }

      this.activeIndex = index;
      this.tabs[index].classList.add('is-active');
      this.tabs[index].setAttribute('aria-selected', 'true');
      this.tabs[index].setAttribute('tabindex', '0');
      newPanel.classList.add('is-active');

      if (isMobile) {
        newPanel.removeAttribute('aria-hidden');
      } else {
        newPanel.removeAttribute('hidden');
      }

      this.playVideosInPanel(newPanel);

      const tabText = this.tabs[index].querySelector('.tabbed-content__tab-text');
      if (tabText) {
        const hash = this.slugify(tabText.textContent);
        history.replaceState(null, null, `#${hash}`);
      }

      this.updateArrowStates();
      this.announceTabChange(index);
      this.lazyLoadImages(newPanel);
    }, delay);
  }

  updateFlowingIndicator(index) {
    const tab = this.tabs[index];
    if (!tab) return;

    const tabsContainer = this.container.querySelector('.tabbed-content__tabs');
    if (!tabsContainer) return;

    const tabRect = tab.getBoundingClientRect();
    const containerRect = tabsContainer.getBoundingClientRect();
    const left = tabRect.left - containerRect.left;
    const width = tabRect.width;

    tabsContainer.style.setProperty('--indicator-left', `${left}px`);
    tabsContainer.style.setProperty('--indicator-width', `${width}px`);

    const style = document.createElement('style');
    style.textContent = `.tabbed-content__tabs::after{left:${left}px;width:${width}px}`;
    const existing = document.getElementById('tabbed-content-indicator-style');
    if (existing) existing.remove();
    style.id = 'tabbed-content-indicator-style';
    document.head.appendChild(style);
  }

  playVideosInPanel(panel) {
    if (!panel.classList.contains('is-active')) return;

    const videos = panel.querySelectorAll('video[autoplay]');
    videos.forEach(video => {
      video.muted = true;
      if (video.paused) {
        video.play().catch(() => {});
      }
      const toggle = video.closest('.tabbed-content__video-container')?.querySelector('[data-sound-toggle]');
      if (toggle) {
        this.setVideoMuteState(video, toggle, true);
      }
    });

    // Handle lazy-loaded iframes (data-src -> src)
    const lazyIframes = panel.querySelectorAll('.tabbed-content__iframe[data-src]');
    lazyIframes.forEach(iframe => {
      if (!iframe.src && iframe.dataset.src) {
        iframe.src = iframe.dataset.src;
      }
    });

    const iframes = panel.querySelectorAll('.tabbed-content__video-container iframe');
    iframes.forEach(iframe => {
      try {
        iframe.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
        iframe.contentWindow.postMessage('{"method":"play"}', '*');
      } catch (e) {}
    });

    // Reset sound toggle state for iframes
    const iframeToggles = panel.querySelectorAll('[data-sound-toggle-iframe]');
    iframeToggles.forEach(toggle => {
      this.setIframeMuteState(toggle, true);
    });
  }

  bindVideoEvents() {
    const videoContainers = this.container.querySelectorAll('.tabbed-content__video-container');
    videoContainers.forEach(container => {
      const playButton = container.querySelector('.tabbed-content__video-play');
      if (!playButton) return;
      playButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.playVideo(container);
      });
    });
  }

  bindSoundToggleEvents() {
    const soundToggles = this.container.querySelectorAll('[data-sound-toggle]');
    soundToggles.forEach(toggle => {
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleVideoSound(toggle);
      });
    });

    // Iframe sound toggles (YouTube/Vimeo)
    const iframeToggles = this.container.querySelectorAll('[data-sound-toggle-iframe]');
    iframeToggles.forEach(toggle => {
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleIframeSound(toggle);
      });
    });
  }

  toggleIframeSound(toggleButton) {
    const videoContainer = toggleButton.closest('.tabbed-content__video-container');
    const iframe = videoContainer.querySelector('iframe');
    const videoType = videoContainer.dataset.videoType;
    const isMuted = toggleButton.getAttribute('data-muted') === 'true';
    const newMuted = !isMuted;

    if (iframe) {
      try {
        if (videoType === 'youtube') {
          // YouTube API command
          const command = newMuted ? 'mute' : 'unMute';
          iframe.contentWindow.postMessage(JSON.stringify({
            event: 'command',
            func: command,
            args: ''
          }), '*');
        } else if (videoType === 'vimeo') {
          // Vimeo API command
          iframe.contentWindow.postMessage(JSON.stringify({
            method: 'setMuted',
            value: newMuted
          }), '*');
        }
      } catch (e) {
        console.log('Iframe mute toggle error:', e);
      }
    }

    this.setIframeMuteState(toggleButton, newMuted);
  }

  setIframeMuteState(toggleButton, muted) {
    const mutedIcon = toggleButton.querySelector('.sound-icon--muted');
    const unmutedIcon = toggleButton.querySelector('.sound-icon--unmuted');

    toggleButton.setAttribute('data-muted', muted ? 'true' : 'false');
    toggleButton.setAttribute('aria-pressed', muted ? 'true' : 'false');

    if (muted) {
      if (mutedIcon) mutedIcon.style.display = 'block';
      if (unmutedIcon) unmutedIcon.style.display = 'none';
      toggleButton.setAttribute('aria-label', 'Toggle audio (currently muted)');
    } else {
      if (mutedIcon) mutedIcon.style.display = 'none';
      if (unmutedIcon) unmutedIcon.style.display = 'block';
      toggleButton.setAttribute('aria-label', 'Toggle audio (currently playing)');
    }
  }

  toggleVideoSound(toggleButton) {
    const videoContainer = toggleButton.closest('.tabbed-content__video-container');
    const video = videoContainer.querySelector('video');
    if (!video) return;
    const isMuted = toggleButton.getAttribute('data-muted') === 'true';
    this.setVideoMuteState(video, toggleButton, !isMuted);
  }

  setVideoMuteState(video, toggleButton, muted) {
    const mutedIcon = toggleButton.querySelector('.sound-icon--muted');
    const unmutedIcon = toggleButton.querySelector('.sound-icon--unmuted');

    video.muted = muted;
    toggleButton.setAttribute('data-muted', muted ? 'true' : 'false');
    toggleButton.setAttribute('aria-pressed', muted ? 'true' : 'false');

    if (muted) {
      if (mutedIcon) mutedIcon.style.display = 'block';
      if (unmutedIcon) unmutedIcon.style.display = 'none';
      toggleButton.setAttribute('aria-label', 'Toggle audio (currently muted)');
    } else {
      if (mutedIcon) mutedIcon.style.display = 'none';
      if (unmutedIcon) unmutedIcon.style.display = 'block';
      toggleButton.setAttribute('aria-label', 'Toggle audio (currently playing)');
    }
  }

  setupVideoVisibilityObserver() {
    const observerOptions = {
      root: null,
      rootMargin: '-10% 0px',
      threshold: 0.1
    };

    this.videoObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const videoContainer = entry.target;
        const video = videoContainer.querySelector('video');
        const soundToggle = videoContainer.querySelector('[data-sound-toggle]');
        const panel = videoContainer.closest('.tabbed-content__panel');

        if (!video) return;

        const isActivePanel = panel && panel.classList.contains('is-active');

        if (!entry.isIntersecting) {
          if (!video.muted && soundToggle) {
            this.setVideoMuteState(video, soundToggle, true);
          }
          video.muted = true;
          if (!video.paused) {
            video.pause();
          }
        } else if (isActivePanel) {
          video.muted = true;
          if (video.paused) {
            video.play().catch(() => {});
          }
          if (soundToggle) {
            this.setVideoMuteState(video, soundToggle, true);
          }
        }
      });
    }, observerOptions);

    const videoContainers = this.container.querySelectorAll('.tabbed-content__video-container[data-video-type="upload"]');
    videoContainers.forEach(container => {
      this.videoObserver.observe(container);
    });
  }

  setupPageVisibilityHandler() {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pauseAllVideos();
      } else {
        const activePanel = this.panels[this.activeIndex];
        if (activePanel) {
          this.playVideosInPanel(activePanel);
        }
      }
    });
  }

  pauseAllVideos() {
    const allVideos = this.container.querySelectorAll('video');
    allVideos.forEach(video => {
      if (!video.paused) {
        video.pause();
      }
      video.muted = true;
      const toggle = video.closest('.tabbed-content__video-container')?.querySelector('[data-sound-toggle]');
      if (toggle) {
        this.setVideoMuteState(video, toggle, true);
      }
    });

    const allIframes = this.container.querySelectorAll('.tabbed-content__video-container iframe');
    allIframes.forEach(iframe => {
      try {
        iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        iframe.contentWindow.postMessage('{"method":"pause"}', '*');
      } catch (e) {
        const src = iframe.src;
        iframe.src = '';
        iframe.src = src.replace('autoplay=1', 'autoplay=0');
      }
    });
  }

  stopAllVideosInPanel(panel) {
    const videos = panel.querySelectorAll('video');
    videos.forEach(video => {
      video.muted = true;
      if (!video.paused) {
        video.pause();
      }
      const toggle = video.closest('.tabbed-content__video-container')?.querySelector('[data-sound-toggle]');
      if (toggle) {
        this.setVideoMuteState(video, toggle, true);
      }
    });

    const iframes = panel.querySelectorAll('.tabbed-content__video-container iframe');
    iframes.forEach(iframe => {
      const container = iframe.closest('.tabbed-content__video-container');
      const videoType = container?.dataset.videoType;
      try {
        if (videoType === 'youtube') {
          iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
          iframe.contentWindow.postMessage('{"event":"command","func":"mute","args":""}', '*');
        } else if (videoType === 'vimeo') {
          iframe.contentWindow.postMessage('{"method":"pause"}', '*');
          iframe.contentWindow.postMessage('{"method":"setMuted","value":true}', '*');
        }
      } catch (e) {}
    });

    // Reset iframe sound toggles
    const iframeToggles = panel.querySelectorAll('[data-sound-toggle-iframe]');
    iframeToggles.forEach(toggle => {
      this.setIframeMuteState(toggle, true);
    });
  }

  enforceActiveVideoState() {
    this.panels.forEach((panel, index) => {
      if (index !== this.activeIndex) {
        this.stopAllVideosInPanel(panel);
      }
    });
  }

  playVideo(container) {
    const panel = container.closest('.tabbed-content__panel');
    if (!panel || !panel.classList.contains('is-active')) return;

    const videoType = container.dataset.videoType;
    const videoUrl = container.dataset.videoUrl;
    let videoId = container.dataset.videoId || '';
    let embedUrl = '';
    
    if (videoType === 'youtube') {
      if (!videoId && videoUrl) {
        videoId = this.extractYoutubeId(videoUrl);
      }
      if (videoId) {
        embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&playsinline=1&enablejsapi=1`;
      }
    } else if (videoType === 'vimeo') {
      if (!videoId && videoUrl) {
        videoId = this.extractVimeoId(videoUrl);
      }
      if (videoId) {
        embedUrl = `https://player.vimeo.com/video/${videoId}?autoplay=1&playsinline=1`;
      }
    }
    
    if (!embedUrl) return;
    
    container.classList.add('is-playing');
    
    const iframe = document.createElement('iframe');
    iframe.src = embedUrl;
    iframe.width = '100%';
    iframe.height = '100%';
    iframe.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;border:0;';
    iframe.setAttribute('frameborder', '0');
    iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
    iframe.allowFullscreen = true;
    iframe.setAttribute('title', 'Video player');
    
    container.innerHTML = '';
    container.appendChild(iframe);
  }

  extractYoutubeId(url) {
    if (!url) return '';
    const patterns = [
      /(?:youtube\.com\/watch\?v=)([^&\n?#]+)/,
      /(?:youtu\.be\/)([^&\n?#]+)/,
      /(?:youtube\.com\/embed\/)([^&\n?#]+)/,
      /(?:youtube\.com\/v\/)([^&\n?#]+)/,
      /(?:youtube\.com\/shorts\/)([^&\n?#]+)/
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return '';
  }

  extractVimeoId(url) {
    if (!url) return '';
    const patterns = [
      /vimeo\.com\/(\d+)/,
      /player\.vimeo\.com\/video\/(\d+)/
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return '';
  }

  handleInitialHash() {
    const hash = window.location.hash.slice(1);
    if (!hash) return;
    this.tabs.forEach((tab, index) => {
      const tabText = tab.querySelector('.tabbed-content__tab-text');
      if (tabText && this.slugify(tabText.textContent) === hash) {
        this.activateTab(index);
      }
    });
  }

  createLiveRegion() {
    const liveRegion = document.createElement('div');
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.classList.add('sr-only');
    liveRegion.id = 'tabbed-content-announcement';
    this.container.appendChild(liveRegion);
    this.liveRegion = liveRegion;
  }

  announceTabChange(index) {
    if (!this.liveRegion) return;
    const tabText = this.tabs[index].querySelector('.tabbed-content__tab-text');
    if (tabText) {
      this.liveRegion.textContent = `${tabText.textContent} tab activated`;
      setTimeout(() => {
        this.liveRegion.textContent = '';
      }, 1000);
    }
  }

  lazyLoadImages(panel) {
    const images = panel.querySelectorAll('img[loading="lazy"]');
    images.forEach(img => {
      if (img.dataset.src && !img.src) {
        img.src = img.dataset.src;
      }
    });
  }

  slugify(text) {
    return text.toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/[\s_-]+/g, '-').replace(/^-+|-+$/g, '');
  }

  destroy() {
    if (this.videoObserver) {
      this.videoObserver.disconnect();
    }
    this.pauseAllVideos();
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.tabbed-content').forEach(section => {
    new TabbedContent(section);
  });
});

document.addEventListener('shopify:section:load', (event) => {
  const section = event.target;
  if (section.classList.contains('tabbed-content')) {
    new TabbedContent(section);
  }
});

document.addEventListener('shopify:section:unload', (event) => {
  const section = event.target;
  if (section.classList.contains('tabbed-content') && section._tabbedContentInstance) {
    section._tabbedContentInstance.destroy();
  }
});

window.addEventListener('hashchange', () => {
  document.querySelectorAll('.tabbed-content').forEach(section => {
    if (section._tabbedContentInstance) {
      section._tabbedContentInstance.handleInitialHash();
    }
  });
});
